import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LEGAL_ROUTES, legalDocListResponseSchema, type LegalDoc } from "@nh/shared";
import { apiFetch } from "@/shared/api/client";
import { CartProvider } from "@/features/cart";
import { ShopLayout } from "@/features/shop/components/ShopLayout";
import { useShopCurrency } from "@/features/shop/useShopCurrency";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Aviso de privacidad y Términos y condiciones — las dos secciones que
 * el menú del sitio promete (Anahí, 27-ago), en UNA página con anclas
 * (`#privacidad`, `#terminos`): son documentos hermanos y se leen
 * juntos.
 *
 * ⚠️ El TEXTO es un punto de partida serio pero NO revisado por un
 * abogado: describe lo que el sistema de verdad hace (qué datos guarda,
 * qué no, cómo se compra, cómo funcionan la franquicia y el plan de
 * referidos). Antes de producción, Anahí debe pasarlo por revisión
 * legal — sobre todo la parte de comisiones y la LFPDPPP.
 * ═══════════════════════════════════════════════════════════════════
 */
export function LegalPage() {
  return (
    <CartProvider>
      <LegalScreen />
    </CartProvider>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-[22px] font-semibold text-ds-ink">{title}</h2>
      <div className="mt-3 grid gap-3 text-[14px] leading-relaxed text-ds-ink-3">
        {children}
      </div>
    </section>
  );
}

/** Un documento GUARDADO desde el panel, con sus párrafos. */
function StoredSection({ doc }: { doc: LegalDoc }) {
  return (
    <Section id={doc.slug} title={doc.title}>
      {doc.content.split(/\n{2,}/).map((paragraph, index) => (
        <p key={index} className="whitespace-pre-line">
          {paragraph}
        </p>
      ))}
    </Section>
  );
}

function LegalScreen() {
  const { t } = useTranslation();
  const [currency, setCurrency] = useShopCurrency();
  const { hash } = useLocation();

  /*
   * Lo GUARDADO desde el panel (`admin/legal-docs`) manda: si un
   * documento existe en la base, se pinta ese; si no, el texto base de
   * abajo sigue siendo el respaldo — la página nunca se queda vacía.
   */
  const docs = useQuery({
    queryKey: ["legal-docs"],
    queryFn: async () => {
      const data = await apiFetch(LEGAL_ROUTES.public, legalDocListResponseSchema, {
        baseUrl: "",
      });
      return data.items;
    },
    staleTime: 5 * 60 * 1000,
  });
  const stored = new Map((docs.data ?? []).map((doc) => [doc.slug, doc]));
  const privacidad = stored.get("privacidad");
  const terminos = stored.get("terminos");
  const envios = stored.get("envios");
  const devoluciones = stored.get("devoluciones");

  /*
   * El menú llega con `#privacidad` o `#terminos`: el desplazamiento al
   * ancla se hace a mano porque la página se monta DESPUÉS de que el
   * navegador ya intentó (y falló) encontrar el ancla.
   */
  useEffect(() => {
    if (!hash) return;
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    // `docs.data` en las dependencias: el ancla puede apuntar a una
    // sección que solo existe cuando los documentos guardados llegan.
  }, [hash, docs.data]);

  return (
    <ShopLayout currency={currency} onCurrencyChange={setCurrency}>
      <div className="mx-auto grid w-full max-w-[820px] gap-12 px-4 py-10 sm:px-8">
        {privacidad ? (
          <StoredSection doc={privacidad} />
        ) : (
        <Section id="privacidad" title={t("Aviso de privacidad")}>
          <p>
            It's The Best (en adelante, "la Plataforma") es responsable del
            tratamiento de los datos personales que nos proporcionas al crear tu
            cuenta y comprar en la tienda, conforme a la Ley Federal de
            Protección de Datos Personales en Posesión de los Particulares.
          </p>
          <p>
            <strong className="text-ds-ink">Qué datos recabamos.</strong> Tu
            nombre y apellidos, correo electrónico, teléfono, país y —cuando
            haces un pedido— la dirección de entrega que nos escribas. Si
            participas en el programa de referidos, también la relación con
            quien te invitó.
          </p>
          <p>
            <strong className="text-ds-ink">Qué NO guardamos.</strong> Los datos
            de tu tarjeta nunca tocan nuestros servidores: los pagos con tarjeta
            se procesan directamente en Stripe, que está certificado para
            manejarlos. Nosotros solo recibimos la confirmación del pago.
          </p>
          <p>
            <strong className="text-ds-ink">Para qué los usamos.</strong> Para
            crear y mantener tu cuenta, procesar y entregar tus pedidos,
            validar tus pagos, calcular las comisiones del plan de referidos
            cuando participas en él, y contactarte sobre tus compras. No
            vendemos ni compartimos tus datos con terceros ajenos a la
            operación (mensajería para entregas y Stripe para pagos).
          </p>
          <p>
            <strong className="text-ds-ink">Tus derechos (ARCO).</strong>{" "}
            Puedes solicitar el acceso, rectificación, cancelación u oposición
            del tratamiento de tus datos escribiéndonos desde la sección de
            Soporte. Responderemos en los plazos que marca la ley.
          </p>
          <p className="text-[12px]">
            {t("Última actualización: 27 de agosto de 2026.")}
          </p>
        </Section>
        )}

        {terminos ? (
          <StoredSection doc={terminos} />
        ) : (
        <Section id="terminos" title={t("Términos y condiciones")}>
          <p>
            Al crear una cuenta y comprar en It's The Best aceptas estos
            términos. Léelos: son el trato entre tú y la Plataforma.
          </p>
          <p>
            <strong className="text-ds-ink">1 · Compras y pagos.</strong> Los
            precios se muestran en la moneda que elijas (MXN o USD) e incluyen
            lo que la pantalla dice, sin cargos ocultos. Puedes pagar con
            tarjeta (vía Stripe), transferencia o efectivo; todo pago se
            valida antes de procesar el pedido, y tu folio (ITB-ORD-…) es el
            comprobante de seguimiento.
          </p>
          <p>
            <strong className="text-ds-ink">2 · Envíos.</strong> Los costos y
            tiempos de entrega se coordinan al confirmar tu pago. Los
            productos son suplementos alimenticios: no son medicamentos y no
            sustituyen una dieta equilibrada ni tratamiento médico.
          </p>
          <p>
            <strong className="text-ds-ink">3 · Membresías y paquetes.</strong>{" "}
            Al validarse tu primera compra te conviertes en socio. Los paquetes
            de recompra personal ofrecen hasta 15% de descuento; los paquetes
            mayoristas (negocio digital), hasta 30% y son exclusivos de
            franquiciatarios.
          </p>
          <p>
            <strong className="text-ds-ink">4 · Franquicia Digital.</strong> La
            franquicia tiene vigencia de un año e incluye tu tienda
            personalizada dentro de la Plataforma. Solo puedes vender los
            productos adquiridos en tus paquetes mayoristas, con el inventario
            que estos te dan. La activación ocurre al validarse tu pago.
          </p>
          <p>
            <strong className="text-ds-ink">5 · Plan de referidos.</strong> Las
            comisiones se pagan conforme al plan vigente publicado en la
            sección de Franquicias Digitales. Habilitar la venta de paquetes en
            tu tienda personalizada genera los bonos del plan de referidos de
            It's The Best, no un sobreprecio sobre los paquetes. El pago de
            comisiones requiere completar la verificación de identidad (KYC).
          </p>
          <p>
            <strong className="text-ds-ink">6 · Cuenta y seguridad.</strong>{" "}
            Eres responsable de mantener tu contraseña en privado. Podemos
            suspender cuentas que abusen de la Plataforma, falsifiquen pagos o
            revendan fuera de las reglas de la franquicia.
          </p>
          <p className="text-[12px]">
            {t("Última actualización: 27 de agosto de 2026.")}
          </p>
        </Section>
        )}

        {/* Envíos y devoluciones existen SOLO guardados: sin texto base
            que fingir mientras Anahí no los escriba. */}
        {envios && <StoredSection doc={envios} />}
        {devoluciones && <StoredSection doc={devoluciones} />}
      </div>
    </ShopLayout>
  );
}
